import React from "react";
import "./HTML_MAIN_CSS.css";

const HTML_WebWorkers = () => {
  return (
    <div className="html-container">
      <h1>HTML Web Workers</h1>

      {/* 1. Introduction */}
      <section className="html-section">
        <h2>1. Introduction</h2>
        <p>
          The <strong>HTML Web Workers API</strong> allows JavaScript to run in the background, independently of the main page.
          This improves performance by preventing long-running scripts from freezing or slowing down the user interface.
        </p>
        <p>
          In simple terms, a web worker runs scripts in a **separate thread**, allowing the browser to handle multiple tasks
          simultaneously without lag.
        </p>
      </section>

      {/* 2. Why Use Web Workers */}
      <section className="html-section">
        <h2>2. Why Use Web Workers?</h2>
        <ul>
          <li>To prevent blocking the main thread during heavy computations.</li>
          <li>To make web applications faster and more responsive.</li>
          <li>To perform background tasks like data processing or API fetching.</li>
          <li>To keep animations, user input, and UI updates smooth.</li>
        </ul>
      </section>

      {/* 3. Basic Syntax */}
      <section className="html-section">
        <h2>3. Basic Syntax</h2>
        <pre className="code-block">
          <code>{`// Check if browser supports Web Workers
if (window.Worker) {
  const worker = new Worker("worker.js");
  worker.postMessage("Start Task"); // Send message to worker

  // Receive message from worker
  worker.onmessage = function(event) {
    console.log("Message from Worker:", event.data);
  };
}`}</code>
        </pre>
      </section>

      {/* 4. Example - Main File */}
      <section className="html-section">
        <h2>4. Example: Main Script (index.html)</h2>
        <pre className="code-block">
          <code>{`<!DOCTYPE html>
<html>
<body>
  <h2>HTML Web Worker Example</h2>
  <button onclick="startWorker()">Start Worker</button>
  <button onclick="stopWorker()">Stop Worker</button>
  <p>Counter: <span id="count"></span></p>

  <script>
  let worker;

  function startWorker() {
    if (typeof(Worker) !== "undefined") {
      if (!worker) {
        worker = new Worker("worker.js");
      }
      worker.onmessage = function(event) {
        document.getElementById("count").innerHTML = event.data;
      };
    } else {
      alert("Sorry! Web Workers are not supported in your browser.");
    }
  }

  function stopWorker() {
    worker.terminate();
    worker = undefined;
  }
  </script>
</body>
</html>`}</code>
        </pre>
      </section>

      {/* 5. Example - Worker File */}
      <section className="html-section">
        <h2>5. Example: Worker Script (worker.js)</h2>
        <pre className="code-block">
          <code>{`let i = 0;

function timedCount() {
  i++;
  postMessage(i); // Send message back to main thread
  setTimeout(timedCount, 1000);
}

timedCount();`}</code>
        </pre>
        <p>
          The above code continuously counts numbers in the background without freezing the main page.
        </p>
      </section>

      {/* 6. Communicating with Workers */}
      <section className="html-section">
        <h2>6. Communicating with Workers</h2>
        <p>
          The main script and the worker communicate using the <code>postMessage()</code> method and
          <code>onmessage</code> event.
        </p>
        <pre className="code-block">
          <code>{`// Main script
worker.postMessage("Hello");

// Worker script
onmessage = function(event) {
  console.log("Received:", event.data);
  postMessage("Hello back from Worker!");
};`}</code>
        </pre>
      </section>

      {/* 7. Terminating Workers */}
      <section className="html-section">
        <h2>7. Terminating Workers</h2>
        <p>
          Web Workers can be manually stopped using the <code>terminate()</code> method.
        </p>
        <pre className="code-block">
          <code>{`// Main thread
worker.terminate();`}</code>
        </pre>
      </section>

      {/* 8. Limitations */}
      <section className="html-section">
        <h2>8. Limitations of Web Workers</h2>
        <ul>
          <li>Cannot access the DOM directly.</li>
          <li>Cannot use alert(), confirm(), or prompt().</li>
          <li>Can only communicate through <code>postMessage()</code>.</li>
          <li>Must be served from the same origin (security restriction).</li>
          <li>Work best with separate script files, not inline code.</li>
        </ul>
      </section>

      {/* 9. Use Cases */}
      <section className="html-section">
        <h2>9. Common Use Cases</h2>
        <ul>
          <li>Background data processing</li>
          <li>Large mathematical calculations</li>
          <li>Handling image or video processing</li>
          <li>Performing complex API requests</li>
          <li>Machine learning computations in the browser</li>
        </ul>
      </section>

      {/* 10. Advanced: Shared and Service Workers */}
      <section className="html-section">
        <h2>10. Advanced: Shared and Service Workers</h2>
        <ul>
          <li>
            <strong>Shared Workers:</strong> Can be shared by multiple browser tabs from the same domain.
          </li>
          <li>
            <strong>Service Workers:</strong> Used for background sync, push notifications, and offline caching.
          </li>
        </ul>
        <pre className="code-block">
          <code>{`// Example: Registering a Service Worker
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/service-worker.js")
  .then(() => console.log("Service Worker Registered"));
}`}</code>
        </pre>
      </section>

      {/* 11. Browser Support */}
      <section className="html-section">
        <h2>11. Browser Support</h2>
        <p>
          Web Workers are supported by all modern browsers:
        </p>
        <ul>
          <li>Google Chrome ✅</li>
          <li>Mozilla Firefox ✅</li>
          <li>Safari ✅</li>
          <li>Edge ✅</li>
          <li>Opera ✅</li>
        </ul>
      </section>

      {/* 12. Summary */}
      <section className="html-section">
        <h2>12. Summary</h2>
        <ul>
          <li>Web Workers run scripts in background threads.</li>
          <li>They keep the main page responsive during long tasks.</li>
          <li>Use <code>postMessage()</code> for communication.</li>
          <li>Cannot directly access the DOM.</li>
          <li>Essential for performance-heavy applications.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTML_WebWorkers;
